import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  InputAdornment,
  Tooltip
} from "@mui/material";
import { AiOutlineClose, AiOutlineSearch } from "react-icons/ai";
import { PiFileCsv } from "react-icons/pi";
import { useQuery } from "@tanstack/react-query";
import { fetchDiscrepancyBreakdown } from "../../api/dashboardApi";
import dayjs from "dayjs";

const DiscrepancyBreakdownModal = ({
  open,
  onClose,
  initialFilters,
  discrepancyType,
  formatNumber,
}) => {
  // Search and Pagination states
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleReset = () => {
    setSearchQuery("");
    setPage(0);
  };

  const currentFilters = {
    vendor_id: initialFilters.vendor_id,
    discrepancy_type: discrepancyType
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["discrepancyBreakdown", currentFilters],
    queryFn: () => fetchDiscrepancyBreakdown(currentFilters),
    enabled: open && !!discrepancyType,
  });

  const breakdownData = data?.data?.invoices || [];
  const totalDiscrepancy = data?.data?.total_discrepancy || 0;
  const totalInvoicesCount = data?.data?.total_invoices || 0;

  const handleDownloadCSV = () => {
    if (!breakdownData.length) return;

    // Define columns for CSV
    const headers = ["Invoice ID", "Date", "Location ID", "Service Count", "Invoice Line Count", "Discrepancy Amount"];
    const csvRows = [headers.join(",")];

    breakdownData.forEach(row => {
      const values = [
        `"${row.invoice_id}"`,
        `"${row.invoice_date}"`,
        `"${row.location_id}"`,
        row.service_count,
        row.invoice_line_count,
        row.discrepancy_amount
      ];
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${discrepancyType}_Discrepancy_Breakdown_${dayjs().format("YYYYMMDD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter based on search (invoice_id)
  const filteredData = breakdownData.filter((item) =>
    (item.invoice_id || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          textTransform: "capitalize"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {discrepancyType} Discrepancy Breakdown
          {totalInvoicesCount > 0 && (
            <Box
              component="span"
              sx={{
                backgroundColor: "#F8EAFF",
                color: "#7C2EA0",
                fontSize: "0.85rem",
                padding: "2px 10px",
                borderRadius: "12px",
                fontWeight: 600,
                border: "1px solid #7C2EA0",
              }}
            >
              {totalInvoicesCount} Invoices
            </Box>
          )}
          {totalDiscrepancy !== 0 && (
            <Tooltip 
              title={`Total Discrepancy: ${totalDiscrepancy.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
              arrow 
              placement="top"
            >
              <Box
                component="span"
                sx={{
                  backgroundColor: "#fff",
                  color: "#7C2EA0",
                  fontSize: "0.85rem",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  fontWeight: 600,
                  border: "1px solid #7C2EA0",
                }}
              >
                {formatNumber ? formatNumber(totalDiscrepancy) : totalDiscrepancy.toFixed(2)}
              </Box>
            </Tooltip>
          )}
        </Box>
        <IconButton onClick={onClose}>
          <AiOutlineClose />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* 🔹 Filter Bar */}
        <Box
          sx={{
            mb: 2,
            pt: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-end",
            flexWrap: "nowrap",
          }}
        >
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search Invoice ID..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 250,
              "& .MuiInputBase-root": { height: "32px", fontSize: "13px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AiOutlineSearch size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {searchQuery !== "" && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleReset}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    paddingInline: "10px",
                    height: "32px",
                  }}
                >
                  Reset
                </Button>
              )}
              
              <Tooltip title="Download CSV" placement="top" arrow>
                <span
                  onClick={handleDownloadCSV}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f5f5f5",
                    cursor: breakdownData.length > 0 ? "pointer" : "default",
                    opacity: breakdownData.length > 0 ? 1 : 0.5,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (breakdownData.length > 0) {
                      e.currentTarget.style.backgroundColor = "#e0e0e0";
                    }
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <PiFileCsv size={18} color="#333" />
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="error">
                {error?.message || "Failed to load discrepancy breakdown."}
              </Typography>
            </Box>
          ) : filteredData.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "50vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Invoice ID
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Location ID
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Service Count
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Invoice Line Count
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor: "#7C2EA0",
                          color: "#fff",
                          fontWeight: 700,
                          zIndex: 11
                        }}
                      >
                        Discrepancy Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((row, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{row.invoice_id}</TableCell>
                        <TableCell>{row.invoice_date}</TableCell>
                        <TableCell>{row.location_id}</TableCell>
                        <TableCell align="right">{row.service_count}</TableCell>
                        <TableCell align="right">{row.invoice_line_count}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: discrepancyType === 'positive' ? '#2e7d32' : '#c62828' }}>
                          {row.discrepancy_amount?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 8, textAlign: "center" }}>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{ fontStyle: "italic" }}
              >
                No data available matching your search.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DiscrepancyBreakdownModal;
